import { faPenToSquare, faPlusCircle, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createPlatformBlock,
  deletePlatformBlock,
  editPlatformBlock,
  getPlatformBlock,
  getPlatformBlocks
} from 'api/platformBlocks';
import {
  createPlatformCriterion,
  deletePlatformCriterion
} from 'api/platformCriteria';
import {
  createPlatformImplementationStep,
  deletePlatformImplementationStep
} from 'api/platformImplementationSteps';
import { getPlatformStages } from 'api/platformStages';
import { getPlatformThemes } from 'api/platformThemes';
import { Flex, Line, Table } from 'components';
import { Modal } from 'components/Modal/Modal';
import { Button, FieldLabel, Select, TextField, TextareaField } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { showPlatformApiError } from 'views/PlatformView/platformApiError';
import {
  IPlatformBlock,
  IPlatformCriterion,
  IPlatformImplementationStep,
  IPlatformStage,
  IPlatformTheme
} from 'types/platformResources';

const RowActions = styled(Flex)`
  gap: 8px;
  justify-content: center;
`;

interface Props {
  practiceId: number;
}

export const PracticeBlocksTab: React.FC<Props> = ({ practiceId }) => {
  const { t } = useTranslation();
  const [blocks, setBlocks] = useState<IPlatformBlock[]>([]);
  const [stages, setStages] = useState<IPlatformStage[]>([]);
  const [themes, setThemes] = useState<IPlatformTheme[]>([]);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [editingBlock, setEditingBlock] = useState<IPlatformBlock>();
  const [blockName, setBlockName] = useState('');
  const [blockCode, setBlockCode] = useState('');
  const [blockColumn, setBlockColumn] = useState('1');
  const [stageId, setStageId] = useState('');
  const [themeId, setThemeId] = useState('');
  const [criteria, setCriteria] = useState<IPlatformCriterion[]>([]);
  const [steps, setSteps] = useState<IPlatformImplementationStep[]>([]);
  const [newCriterion, setNewCriterion] = useState('');
  const [newStepName, setNewStepName] = useState('');
  const [newStepDesc, setNewStepDesc] = useState('');

  const stageIds = useMemo(() => new Set(stages.map((s) => s.id)), [stages]);
  const themeIds = useMemo(() => new Set(themes.map((th) => th.id)), [themes]);

  const practiceBlocks = useMemo(
    () =>
      blocks.filter(
        (block) => stageIds.has(block.stage_id) && themeIds.has(block.theme_id)
      ),
    [blocks, stageIds, themeIds]
  );

  const stageOptions: IOption[] = stages.map((s) => ({
    label: `${s.number}. ${s.name}`,
    value: String(s.id)
  }));

  const themeOptions: IOption[] = themes.map((th) => ({
    label: `${th.number}. ${th.name}`,
    value: String(th.id)
  }));

  const load = useCallback(async () => {
    try {
      const [blocksRes, stagesRes, themesRes] = await Promise.all([
        getPlatformBlocks(),
        getPlatformStages(practiceId),
        getPlatformThemes(practiceId)
      ]);

      setBlocks(blocksRes.data);
      setStages(stagesRes.data);
      setThemes(themesRes.data);
    } catch (error) {
      showPlatformApiError(error, t);
    }
  }, [practiceId, t]);

  useEffect(() => {
    load();
  }, [load]);

  const openAddBlock = () => {
    setEditingBlock(undefined);
    setBlockName('');
    setBlockCode('');
    setBlockColumn('1');
    setStageId(stageOptions[0]?.value || '');
    setThemeId(themeOptions[0]?.value || '');
    setCriteria([]);
    setSteps([]);
    setIsBlockModalOpen(true);
  };

  const openEditBlock = async (block: IPlatformBlock) => {
    try {
      const detail = await getPlatformBlock(block.id);
      const source = detail.data;

      setEditingBlock(source);
      setBlockName(source.name);
      setBlockCode(source.code || '');
      setBlockColumn(String(source.column || 1));
      setStageId(String(source.stage_id));
      setThemeId(String(source.theme_id));
      setCriteria(source.criteria || []);
      setSteps(source.implementation_steps || []);
      setIsBlockModalOpen(true);
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const handleSaveBlock = async () => {
    const payload = {
      name: blockName.trim(),
      code: blockCode.trim(),
      column: Number(blockColumn) || 1,
      stage_id: Number(stageId),
      theme_id: Number(themeId)
    };

    if (!payload.name || !payload.stage_id || !payload.theme_id) {
      toast.error(t('common.fieldRequired'));
      return;
    }

    try {
      if (editingBlock) {
        await editPlatformBlock(editingBlock.id, { block: payload });
      } else {
        await createPlatformBlock({ block: payload });
      }

      setIsBlockModalOpen(false);
      load();
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const handleDeleteBlock = async (id: number) => {
    try {
      await deletePlatformBlock(id);
      load();
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const blockIdForNested = editingBlock?.id;

  const handleAddCriterion = async () => {
    if (!blockIdForNested || !newCriterion.trim()) return;

    try {
      const response = await createPlatformCriterion({
        criterion: { content: newCriterion.trim(), block_id: blockIdForNested }
      });

      setCriteria((prev) => [...prev, response.data]);
      setNewCriterion('');
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const handleAddStep = async () => {
    if (!blockIdForNested || !newStepName.trim()) return;

    try {
      const response = await createPlatformImplementationStep({
        implementation_step: {
          name: newStepName.trim(),
          description: newStepDesc.trim(),
          block_id: blockIdForNested
        }
      });

      setSteps((prev) => [...prev, response.data]);
      setNewStepName('');
      setNewStepDesc('');
    } catch (error) {
      showPlatformApiError(error, t);
    }
  };

  const stageName = (id: number) => stages.find((s) => s.id === id)?.name || id;
  const themeName = (id: number) => themes.find((th) => th.id === id)?.name || id;

  const header = [
    t('platformResources.fields.code'),
    t('platformResources.fields.name'),
    t('platformResources.fields.stage'),
    t('platformResources.fields.theme'),
    ''
  ];

  const data = practiceBlocks.map((block) => ({
    code: block.code || '—',
    name: block.name,
    stage: stageName(block.stage_id),
    theme: themeName(block.theme_id),
    actions: (
      <RowActions key={`block-actions-${block.id}`}>
        <Button
          variant="eucalyptus"
          icon={<FontAwesomeIcon icon={faPenToSquare} />}
          onClick={() => openEditBlock(block)}
        />
        <Button
          variant="red"
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => handleDeleteBlock(block.id)}
        />
      </RowActions>
    )
  }));

  return (
    <>
      <Flex justifyContent="flex-end" mb={2}>
        <Button
          variant="eucalyptus"
          onClick={openAddBlock}
          disabled={!stageOptions.length || !themeOptions.length}
        >
          <FontAwesomeIcon icon={faPlusCircle} /> {t('platformResources.addBlock')}
        </Button>
      </Flex>

      {!stageOptions.length || !themeOptions.length ? (
        <p>{t('platformResources.blocksNeedStagesThemes')}</p>
      ) : (
        <Table header={header} data={data} sortTable={false} rightFixedKeys={[4]} />
      )}

      <Modal
        isOpen={isBlockModalOpen}
        onCancelClick={() => setIsBlockModalOpen(false)}
        headerTitle={
          editingBlock ? t('platformResources.editBlock') : t('platformResources.addBlock')
        }
        mainButton={{
          action: handleSaveBlock,
          variant: 'eucalyptus',
          label: t('buttons.save')
        }}
        isSecondButtonVisible
        gridTemplateColumns="70%"
      >
        <Flex flexDirection="column" gap="12px">
          <TextField
            label={t('platformResources.fields.name')}
            value={blockName}
            onChange={(e) => setBlockName(e.target.value)}
          />
          <TextField
            label={t('platformResources.fields.code')}
            value={blockCode}
            onChange={(e) => setBlockCode(e.target.value)}
          />
          <TextField
            label={t('platformResources.fields.column')}
            type="number"
            value={blockColumn}
            onChange={(e) => setBlockColumn(e.target.value)}
          />
          <Select
            label={t('platformResources.fields.stage')}
            options={stageOptions}
            selectedOptions={stageOptions.find((o) => o.value === stageId)}
            onChange={(o: IOption) => setStageId(o.value)}
          />
          <Select
            label={t('platformResources.fields.theme')}
            options={themeOptions}
            selectedOptions={themeOptions.find((o) => o.value === themeId)}
            onChange={(o: IOption) => setThemeId(o.value)}
          />

          {editingBlock && (
            <>
              <Line />
              <FieldLabel>{t('platformResources.criteria')}</FieldLabel>
              {criteria.map((c) => (
                <Flex key={c.id} gap="8px" alignItems="center">
                  <span style={{ flex: 1 }}>{c.content}</span>
                  <Button
                    variant="red"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={async () => {
                      try {
                        await deletePlatformCriterion(c.id);
                        setCriteria((prev) => prev.filter((x) => x.id !== c.id));
                      } catch (error) {
                        showPlatformApiError(error, t);
                      }
                    }}
                  />
                </Flex>
              ))}
              <Flex gap="8px">
                <TextField
                  value={newCriterion}
                  onChange={(e) => setNewCriterion(e.target.value)}
                  placeholder={t('platformResources.addCriterion')}
                />
                <Button variant="eucalyptus" onClick={handleAddCriterion}>
                  {t('buttons.add')}
                </Button>
              </Flex>

              <FieldLabel>{t('platformResources.implementationSteps')}</FieldLabel>
              {steps.map((s) => (
                <Flex key={s.id} gap="8px" alignItems="center">
                  <span style={{ flex: 1 }}>{s.name}</span>
                  <Button
                    variant="red"
                    icon={<FontAwesomeIcon icon={faTrash} />}
                    onClick={async () => {
                      try {
                        await deletePlatformImplementationStep(s.id);
                        setSteps((prev) => prev.filter((x) => x.id !== s.id));
                      } catch (error) {
                        showPlatformApiError(error, t);
                      }
                    }}
                  />
                </Flex>
              ))}
              <TextField
                label={t('platformResources.fields.name')}
                value={newStepName}
                onChange={(e) => setNewStepName(e.target.value)}
              />
              <TextareaField
                label={t('platformResources.fields.description')}
                value={newStepDesc}
                onChange={(e) => setNewStepDesc(e.target.value)}
              />
              <Button variant="eucalyptus" onClick={handleAddStep}>
                {t('platformResources.addStep')}
              </Button>
            </>
          )}
        </Flex>
      </Modal>
    </>
  );
};
